import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Box } from './box';
import { Flex } from './flex';
import { Text } from './text';
import { RAIL_MARGIN, RAIL_ZOOM } from '../../helpers/utils/screenFit';

interface RailProps {
  /** The rail's accessible name (it's an aside). */
  label: string;
  /** Left of the pinned header, e.g. "ROUND HISTORY". */
  title: string;
  /** Right of the pinned header, e.g. "5 ROUNDS · NEWEST FIRST". */
  meta: string;
  children: ReactNode;
  /** Draw the bottom fade and our own thumb (Game setup rail #7). */
  scrollCues?: boolean;
  /** Body padding, in design points (the content is x1.18). */
  bodyPadding?: string;
  /** Viewers' rails scroll, but their content stays read-only. */
  readOnly?: boolean;
}

// The scroll thumb's track: under the header, inset from the rail's edge.
const TRACK_INSET = 7;

/**
 * A desktop right rail: Emerald/History rail (1728:20701) and Emerald/Game
 * setup rail (1976:21106). 460 wide, from 16 under the header to 24 above the
 * bottom. The header is pinned and only the body scrolls. Its content is the
 * 390 phone design at x1.18.
 */
function Rail({
  label,
  title,
  meta,
  children,
  scrollCues = false,
  bodyPadding,
  readOnly = false,
}: RailProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [cues, setCues] = useState({
    overflow: false,
    atEnd: true,
    top: 0,
    trackTop: 0,
    trackHeight: 0,
    thumbHeight: 0,
  });

  const measure = useCallback(() => {
    const body = bodyRef.current;
    const header = headerRef.current;
    if (!body || !header) return;
    const { scrollTop, scrollHeight, clientHeight } = body;
    const trackTop = header.offsetHeight + TRACK_INSET;
    const trackHeight = Math.max(0, clientHeight - 2 * TRACK_INSET);
    const thumbHeight =
      scrollHeight > 0 ? (trackHeight * clientHeight) / scrollHeight : 0;
    const range = scrollHeight - clientHeight;
    const progress = range > 0 ? scrollTop / range : 0;
    setCues({
      overflow: scrollHeight > clientHeight + 1,
      atEnd: scrollTop >= range - 1,
      top: trackTop + progress * (trackHeight - thumbHeight),
      trackTop,
      trackHeight,
      thumbHeight,
    });
  }, []);

  useLayoutEffect(() => {
    if (scrollCues) measure();
  }, [scrollCues, measure]);

  useEffect(() => {
    if (!scrollCues) return;
    const body = bodyRef.current;
    if (!body || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    if (body.firstElementChild) observer.observe(body.firstElementChild);
    return () => observer.disconnect();
  }, [scrollCues, measure]);

  return (
    <Box
      as="aside"
      aria-label={label}
      position="relative"
      display="flex"
      flexDirection="column"
      h={`calc(100% - ${RAIL_MARGIN}px)`}
      minH={0}
      overflow="hidden"
      bg="var(--spades-background)"
      color="spades.ivory"
      border="1.18px solid var(--spades-border)"
      borderRadius="25.95px"
      // A viewer's board blocks pointer events; the rail must still scroll.
      pointerEvents="auto"
    >
      <Flex
        ref={headerRef}
        data-testid="rail-header"
        flexShrink={0}
        justify="space-between"
        align="center"
        gap="var(--spades-spacing-sm)"
        pt="18px"
        pb="var(--spades-spacing-ms)"
        px="19px"
        style={{ zoom: RAIL_ZOOM }}
      >
        {[title, meta].map((text) => (
          <Text
            key={text}
            fontSize="var(--spades-font-size-2xs)"
            fontWeight="700"
            lineHeight={1.25}
            letterSpacing="var(--spades-tracking-md)"
            color="spades.muted"
            whiteSpace="nowrap"
          >
            {text}
          </Text>
        ))}
      </Flex>
      <Box
        ref={bodyRef}
        data-testid="rail-body"
        className={scrollCues ? 'rail-scroll' : undefined}
        flex="1"
        minH={0}
        overflowY="auto"
        overflowX="hidden"
        overscrollBehavior="contain"
        onScroll={scrollCues ? measure : undefined}
      >
        <Box
          p={bodyPadding}
          pointerEvents={readOnly ? 'none' : undefined}
          style={{ zoom: RAIL_ZOOM }}
        >
          {children}
        </Box>
      </Box>
      {scrollCues && cues.overflow && (
        <>
          {!cues.atEnd && (
            <Box
              data-testid="rail-fade"
              aria-hidden="true"
              position="absolute"
              left={0}
              right={0}
              bottom={0}
              h="85px"
              pointerEvents="none"
              bgGradient="to-b"
              gradientFrom="rgba(2, 18, 30, 0)"
              gradientTo="var(--spades-background)"
            />
          )}
          <Box
            data-testid="rail-track"
            aria-hidden="true"
            position="absolute"
            right="5px"
            w="4px"
            pointerEvents="none"
            style={{ top: cues.trackTop, height: cues.trackHeight }}
          />
          <Box
            data-testid="rail-thumb"
            aria-hidden="true"
            position="absolute"
            right="5px"
            w="4px"
            borderRadius="2px"
            bg="var(--spades-muted)"
            pointerEvents="none"
            style={{ top: cues.top, height: cues.thumbHeight }}
          />
        </>
      )}
    </Box>
  );
}

export default Rail;
